import { createContext, ReactNode, useState, useMemo, useContext } from 'react';
import { ComponentTemplates } from 'global/constants';

import {
  ACTIVITY_BUILDER_DRAWER_ADD_COMPONENT,
  ACTIVITY_BUILDER_DRAWER_GLOBAL_SETTINGS,
  ACTIVITY_BUILDER_DRAWER_HISTORY,
  ACTIVITY_BUILDER_DRAWER_STYLES,
  ACTIVITY_BUILDER_DRAWER_TEACHING_NOTES,
} from './activity-builder.constants';

type ActiveComponentMeta = {
  templateId?: ComponentTemplates;
};

type ActiveComponent = {
  id: string;
  column?: {
    id: string;
    componentId: string;
  };
  meta?: ActiveComponentMeta | undefined;
};

type ActivityBuilder = {
  activeComponent?: ActiveComponent;
  componentDrawerId: string | undefined;
  isDrawerActive: boolean;
  isZoomToFit: boolean;
  croppingComponentId: string;
  slideScaleRatio: number;
  editModeTargetElement?: string;
  closeDrawer: () => void;
  openDrawer: (props?: { componentId?: string }) => void;
  isAddDrawerActive: () => boolean;
  isEditDrawerActive: () => boolean;
  keyboardDragComponentId: string;
  setKeyboardDragComponentId: (mode: string) => void;
  setActiveComponent: (component: ActiveComponent | undefined) => void;
  clearActiveComponent: () => void;
  setComponentDrawerId: (id: string) => void;
  setZoomToFit: (state: boolean) => void;
  setSlideScaleRatio: (ratio: number) => void;
  setCroppingComponentId: (state: string) => void;
  setEditModeTargetElement: (id: string | undefined) => void;
};

export const ActivityBuilderContext = createContext<ActivityBuilder>({
  activeComponent: undefined,
  componentDrawerId: undefined,
  isDrawerActive: false,
  isZoomToFit: true,
  slideScaleRatio: 1,
  croppingComponentId: '',
  closeDrawer: () => {},
  openDrawer: () => {},
  isAddDrawerActive: () => false,
  isEditDrawerActive: () => false,
  keyboardDragComponentId: '',
  setKeyboardDragComponentId: () => {},
  setActiveComponent: () => {},
  setComponentDrawerId: () => {},
  clearActiveComponent: () => {},
  setZoomToFit: () => {},
  setSlideScaleRatio: () => {},
  setCroppingComponentId: () => {},
  editModeTargetElement: undefined,
  setEditModeTargetElement: (id: string | undefined) => {},
});

export const ActivityBuilderContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isDrawerActive, setIsDrawerActive] = useState<boolean>(false);
  const [isZoomToFit, setZoomToFit] = useState<boolean>(true);
  const [croppingComponentId, setCroppingComponentId] = useState<string>('');
  const [slideScaleRatio, setSlideScaleRatio] = useState<number>(1);
  const [editModeTargetElement, setEditModeTargetElement] = useState<
    string | undefined
  >();

  const [componentDrawerId, setComponentDrawerId] = useState<
    string | undefined
  >();
  const [keyboardDragComponentId, setKeyboardDragComponentId] =
    useState<string>('');

  const [activeComponent, setActiveComponent] = useState<
    ActiveComponent | undefined
  >(undefined);

  const value = useMemo(
    () => ({
      activeComponent,
      isDrawerActive,
      closeDrawer: () => {
        setIsDrawerActive(false);
        setComponentDrawerId(undefined);
        setEditModeTargetElement(undefined);
      },
      openDrawer: (props?: { componentId?: string }) => {
        setEditModeTargetElement(undefined);
        setComponentDrawerId(props?.componentId);
        setIsDrawerActive(true);
      },
      keyboardDragComponentId,
      setKeyboardDragComponentId,
      setActiveComponent: (comp: ActiveComponent | undefined) => {
        setEditModeTargetElement(undefined);
        setActiveComponent(comp);
      },
      clearActiveComponent: () => {
        setActiveComponent(undefined);
        setEditModeTargetElement(undefined);
      },
      componentDrawerId,
      setComponentDrawerId,
      isZoomToFit,
      setZoomToFit,
      croppingComponentId,
      setCroppingComponentId,
      slideScaleRatio,
      setSlideScaleRatio,
      isAddDrawerActive: () => {
        return (
          isDrawerActive &&
          componentDrawerId === ACTIVITY_BUILDER_DRAWER_ADD_COMPONENT
        );
      },
      isEditDrawerActive: () => {
        return (
          isDrawerActive &&
          componentDrawerId !== ACTIVITY_BUILDER_DRAWER_TEACHING_NOTES &&
          componentDrawerId !== ACTIVITY_BUILDER_DRAWER_GLOBAL_SETTINGS &&
          componentDrawerId !== ACTIVITY_BUILDER_DRAWER_HISTORY &&
          componentDrawerId !== ACTIVITY_BUILDER_DRAWER_ADD_COMPONENT &&
          componentDrawerId !== ACTIVITY_BUILDER_DRAWER_STYLES
        );
      },
      editModeTargetElement,
      setEditModeTargetElement,
    }),
    [
      activeComponent,
      setActiveComponent,
      isDrawerActive,
      keyboardDragComponentId,
      setKeyboardDragComponentId,
      componentDrawerId,
      setComponentDrawerId,
      isZoomToFit,
      setZoomToFit,
      croppingComponentId,
      setCroppingComponentId,
      slideScaleRatio,
      setSlideScaleRatio,
      editModeTargetElement,
      setEditModeTargetElement,
    ]
  );

  return (
    <ActivityBuilderContext.Provider value={value}>
      {children}
    </ActivityBuilderContext.Provider>
  );
};

export const useActivityBuilderContext = () =>
  useContext(ActivityBuilderContext);
